import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../Auth/Auth';
import {
  MagnifyingGlassIcon,
  BookOpenIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
  BanknotesIcon,
  ShoppingCartIcon,
  HeartIcon,
  ShieldCheckIcon,
  EyeIcon,
  QuestionMarkCircleIcon,
  DocumentTextIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const HelpCenter = () => {
  const { role } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredGuides, setFilteredGuides] = useState([]);

  // Available comprehensive guides
  const guides = useMemo(() => [
    {
      id: 'service-advisor',
      title: 'Service Advisor Guide',
      icon: UserGroupIcon,
      color: 'blue',
      wordCount: '15,000+',
      description: 'Complete guide for managing customers, vehicles, job cards, and service operations',
      roles: ['Service Advisor', 'Admin', 'Supervisor'],
      topics: [
        'Customer & vehicle management',
        'Job card creation (Traditional & DQ)',
        'Task assignment & monitoring',
        'Photo documentation',
        'Vehicle inspection (10-step process)',
        'Daily/weekly/monthly workflows',
      ],
    },
    {
      id: 'mechanic',
      title: 'Mechanic Guide',
      icon: WrenchScrewdriverIcon,
      color: 'green',
      wordCount: '8,000+',
      description: 'Essential guide for mechanics to manage tasks, complete work, and maintain quality',
      roles: ['Mechanic', 'Admin', 'Supervisor'],
      topics: [
        'Task management & execution',
        'Working with job cards',
        'Reporting issues & findings',
        'Time management',
        'Safety & quality standards',
        'Performance & career growth',
      ],
    },
    {
      id: 'finance',
      title: 'Finance Guide',
      icon: BanknotesIcon,
      color: 'yellow',
      wordCount: '18,000+',
      description: 'Comprehensive financial management guide covering quotations, invoices, and reporting',
      roles: ['Finance', 'Admin'],
      topics: [
        'Quotation management',
        'Invoice generation & payment tracking',
        'Receivables & payables management',
        'Commission tracking & calculation',
        'Tax management (VAT, WHT)',
        'Financial analytics & reporting',
      ],
    },
    {
      id: 'procurement',
      title: 'Procurement Guide',
      icon: ShoppingCartIcon,
      color: 'purple',
      wordCount: '20,000+',
      description: 'Complete procurement guide for pricing, inventory, suppliers, and purchase management',
      roles: ['Procurement', 'Admin'],
      topics: [
        'Job card pricing (adding cost prices)',
        'Inventory management & stock control',
        'Supplier management & evaluation',
        'Purchase requisitions & orders',
        'Delivery management & inspection',
        'Procurement analytics',
      ],
    },
    {
      id: 'customer-care',
      title: 'Customer Care Guide',
      icon: HeartIcon,
      color: 'pink',
      wordCount: 'Coming Soon',
      description: 'Guide for managing customer relationships, feedback, and service excellence',
      roles: ['Customer Care', 'Admin'],
      topics: [
        'Customer relationship management',
        'Feedback collection & management',
        'Complaint resolution',
        'Appointment scheduling',
        'Customer analytics',
      ],
      comingSoon: true,
    },
    {
      id: 'admin',
      title: 'Admin Guide',
      icon: ShieldCheckIcon,
      color: 'red',
      wordCount: 'Coming Soon',
      description: 'System administration guide for user management, RBAC, and configuration',
      roles: ['Admin'],
      topics: [
        'User management & employee onboarding',
        'Role-based access control (RBAC)',
        'System configuration',
        'Service/product management',
        'Contractor management',
      ],
      comingSoon: true,
    },
    {
      id: 'supervisor',
      title: 'Supervisor Guide',
      icon: EyeIcon,
      color: 'indigo',
      wordCount: 'Coming Soon',
      description: 'Supervisory guide for team oversight, task monitoring, and quality assurance',
      roles: ['Supervisor', 'Admin'],
      topics: [
        'Team oversight & task monitoring',
        'Performance tracking',
        'Quality assurance & verification',
        'Resource allocation',
        'Performance analytics',
      ],
      comingSoon: true,
    },
  ], []);

  // Filter guides based on user role and search query
  useEffect(() => {
    let filtered = guides;

    // Filter by role (show guides relevant to user's role)
    if (role && role !== 'Admin') {
      filtered = filtered.filter((guide) => guide.roles.includes(role));
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (guide) =>
          guide.title.toLowerCase().includes(query) ||
          guide.description.toLowerCase().includes(query) ||
          guide.topics.some((topic) => topic.toLowerCase().includes(query))
      );
    }

    setFilteredGuides(filtered);
  }, [role, searchQuery, guides]);

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-600 border-blue-200',
      green: 'bg-green-100 text-green-600 border-green-200',
      yellow: 'bg-yellow-100 text-yellow-600 border-yellow-200',
      purple: 'bg-purple-100 text-purple-600 border-purple-200',
      pink: 'bg-pink-100 text-pink-600 border-pink-200',
      red: 'bg-red-100 text-red-600 border-red-200',
      indigo: 'bg-indigo-100 text-indigo-600 border-indigo-200',
    };
    return colors[color] || colors.blue;
  };

  const handleGuideClick = (guideId) => {
    navigate(`/help/guide/${guideId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center">
            <BookOpenIcon className="h-16 w-16 text-white mx-auto mb-4" />
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ayden Garage Help Center
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Comprehensive user guides to help you master the Ayden Garage Management System.
              Find detailed documentation for your role and learn best practices.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search guides, topics, or features..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-lg border-2 border-transparent focus:border-white focus:ring-2 focus:ring-white focus:ring-opacity-50 text-gray-900 placeholder-gray-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Stats Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-lg shadow-sm p-6 text-center">
            <DocumentTextIcon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <div className="text-3xl font-bold text-gray-900">4</div>
            <div className="text-sm text-gray-600">Comprehensive Guides</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6 text-center">
            <BookOpenIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <div className="text-3xl font-bold text-gray-900">61,000+</div>
            <div className="text-sm text-gray-600">Words of Documentation</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6 text-center">
            <UserGroupIcon className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <div className="text-3xl font-bold text-gray-900">7</div>
            <div className="text-sm text-gray-600">Role-Specific Guides</div>
          </div>
        </div>

        {/* User Role Banner */}
        {role && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8 rounded">
            <div className="flex items-start">
              <QuestionMarkCircleIcon className="h-6 w-6 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-blue-800">
                  Guides for {role}
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  Below are the guides relevant to your role. Click on any guide to view the complete documentation.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Guides Grid */}
        {filteredGuides.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredGuides.map((guide) => {
              const Icon = guide.icon;
              return (
                <div
                  key={guide.id}
                  className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden ${
                    guide.comingSoon ? 'opacity-60' : 'cursor-pointer'
                  }`}
                  onClick={() => !guide.comingSoon && handleGuideClick(guide.id)}
                >
                  <div className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div
                          className={`p-3 rounded-lg ${getColorClasses(
                            guide.color
                          )} mr-4`}
                        >
                          <Icon className="h-6 w-6" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            {guide.title}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {guide.wordCount}
                            {guide.comingSoon && ' • Coming Soon'}
                          </p>
                        </div>
                      </div>
                      {!guide.comingSoon && (
                        <ArrowRightIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 mb-4">{guide.description}</p>

                    {/* Topics */}
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">
                        Topics Covered:
                      </h4>
                      <ul className="space-y-1.5">
                        {guide.topics.slice(0, 4).map((topic, index) => (
                          <li
                            key={index}
                            className="text-sm text-gray-600 flex items-start"
                          >
                            <span className="text-blue-600 mr-2">•</span>
                            <span>{topic}</span>
                          </li>
                        ))}
                        {guide.topics.length > 4 && (
                          <li className="text-sm text-gray-500 italic pl-3">
                            + {guide.topics.length - 4} more topics
                          </li>
                        )}
                      </ul>
                    </div>

                    {/* Roles */}
                    <div className="flex flex-wrap gap-2">
                      {guide.roles.map((roleName, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {roleName}
                        </span>
                      ))}
                    </div>
                  </div>

                  {!guide.comingSoon && (
                    <div className="bg-gray-50 px-6 py-3 border-t border-gray-100">
                      <button className="text-sm font-medium text-blue-600 hover:text-blue-700">
                        View Complete Guide →
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <MagnifyingGlassIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No guides found
            </h3>
            <p className="text-gray-600">
              Try adjusting your search query or check back later for new guides.
            </p>
          </div>
        )}

        {/* Additional Resources */}
        <div className="mt-12 bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Additional Resources
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <a
              href="/help/accounts-guide"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-yellow-500 hover:bg-yellow-50 transition-colors"
            >
              <BanknotesIcon className="h-8 w-8 text-yellow-500 mr-4" />
              <div>
                <h4 className="font-medium text-gray-900">Accounts Quick Guide</h4>
                <p className="text-sm text-gray-600">
                  Quotations, invoices & payments
                </p>
              </div>
            </a>
            <a
              href="/help/procurement-guide"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors"
            >
              <ShoppingCartIcon className="h-8 w-8 text-purple-400 mr-4" />
              <div>
                <h4 className="font-medium text-gray-900">Procurement Process Guide</h4>
                <p className="text-sm text-gray-600">
                  Visual guide with flowcharts & FAQs
                </p>
              </div>
            </a>
            <a
              href="/help/articles"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <DocumentTextIcon className="h-8 w-8 text-gray-400 mr-4" />
              <div>
                <h4 className="font-medium text-gray-900">Help Articles</h4>
                <p className="text-sm text-gray-600">
                  Browse quick tips and FAQs
                </p>
              </div>
            </a>
            <a
              href="/help/system-overview"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <BookOpenIcon className="h-8 w-8 text-gray-400 mr-4" />
              <div>
                <h4 className="font-medium text-gray-900">System Overview</h4>
                <p className="text-sm text-gray-600">
                  Understanding roles and workflows
                </p>
              </div>
            </a>
          </div>
        </div>

        {/* Need More Help */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Need More Help?
          </h3>
          <p className="text-gray-700 mb-4">
            Can't find what you're looking for? Contact your supervisor or IT support.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
              Contact Support
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
              Report an Issue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
